import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  activateCategory,
  categoriesShellQuery,
  categoryQuery,
  categorySubcategoriesQuery,
  categoryTableQuery,
  createCategory,
  removeCategory,
  saveCategory,
  type CategoryTicketType,
} from '../../api/settingsEaseedeskCategories'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Categories, intreg: citire SI scriere.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * ticket_categories/index.php` + `subcategories.php` + `subcategoryList.php`,
 * coloana cu coloana fata de `categoriesModel` din
 * `public/js/settings/easeedesk/tickets_settings.js`, si gest cu gest fata de
 * `public/js/views/easeedesk_settings/tickets/ticket_categories/index-1.js`:
 *
 *   Create new category  ->  POST /settings/easeedesk/ticket_categories
 *   Save (modalul Edit)  ->  POST .../ticket_categories/{id}
 *   bifa Activate        ->  POST .../ticket_categories/{id}/activate
 *   Remove               ->  POST .../ticket_categories/{id}/remove
 *
 * Toate cer acelasi drept ca in portalul vechi
 * (`settings.easeedeskticket_ticket_categories_settings.read`) si sunt marginite
 * pe firma in fiecare interogare - vezi `EaseedeskCategories`.
 *
 * DE CE CLICUL SE PRINDE PE INVELIS
 * ---------------------------------
 *
 * Celulele listei sunt HTML scris de coloana si pus cu `innerHTML`, exact ca in
 * portal - nu noduri React. Deci butoanele din ele n-au cum sa aiba `onClick`
 * propriu, si clicul se prinde sus, pe delegare, cum il prindea jQuery pe
 * `.edit-category` / `.remove-category` / `input.activate-category`.
 *
 * Bifa se citeste DUPA clic (`input.checked`), fiindca browserul o comuta
 * inainte sa trimita evenimentul - la fel ca `ifToggled` din iCheck.
 */

const TABLE_ID = 'ticket-categories'

/** `Ticket_category::categoryActionsForm()`, litera cu litera. */
export function categoryActionsHtml(id: number): string {
  return (
    '<div class="btn-group" role="group" aria-label="...">\n' +
    '                    <button type="button" data-category="' +
    id +
    '" class="btn btn-sm btn-default edit-category" data-toggle="modal" data-target="#editCategoryModal"><i class="fas fa-pencil-alt" aria-hidden="true"></i> Edit</button>\n' +
    '                    <button type="button" data-category="' +
    id +
    '" class="btn btn-sm btn-default remove-category"><i class="fas fa-times" aria-hidden="true"></i> Remove</button>\n' +
    '                </div>'
  )
}

export function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** `categoriesModel` din tickets_settings.js: aceleasi cinci coloane, in aceeasi ordine. */
export function categoryColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 30,
      html: (row) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(row.ID)}" data-type=""><i class="fas fa-plus-circle"></i></center>`,
    },
    {
      index: 1,
      title: 'Category',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(String(row.category_name ?? '')),
    },
    {
      index: 2,
      title: 'Activate',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) =>
        `<input type="checkbox" ${Number(row.active) === 1 ? 'checked' : ''} class="activate-category" value="${Number(row.ID)}"/>`,
    },
    {
      index: 3,
      title: 'Subcategories',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => `<span class="label label-info">${Number(row.subcategories ?? 0)}</span>`,
    },
    {
      index: 4,
      title: 'Actions',
      orderable: false,
      visible: true,
      width: 200,
      className: 'center',
      html: (row) => categoryActionsHtml(Number(row.ID)),
    },
  ]
}

/** Coloana SQL a fiecarui indice, ca in harta din `Ticket_category::getCategoryList()`. */
export const ORDER_NAMES = ['ID', 'category_name', 'active', 'ID', 'ID']

export function TicketCategoriesPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(categoriesShellQuery())
  /* `order: [[1, 'desc']]` - pe ce si cum porneste lista veche. */
  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: 25,
    order: { column: 1, dir: 'desc' },
    search: '',
  })
  const [edited, setEdited] = useState(0)
  const [creating, setCreating] = useState(false)
  const columns = useMemo(() => categoryColumns(), [])
  const table = useQuery({
    ...categoryTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: ORDER_NAMES[state.order.column] ?? 'ID',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  /** `$datatable.draw()` din portal: lista se cere din nou dupa fiecare scriere. */
  const redraw = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'ticket_categories'] })
  }

  const activate = useMutation({
    mutationFn: (v: { category: number; active: boolean }) => activateCategory(v.category, v.active),
    onSuccess: redraw,
    onError: () => {
      toast('Something went wrong.', 'Error', 'error')
      redraw()
    },
  })

  const remove = useMutation({
    mutationFn: (category: number) => removeCategory(category),
    onSuccess: redraw,
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (shell.error) {
    return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="This screen" />
  }
  if (!shell.data) return <Spinner />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement

    const edit = target.closest('.edit-category')
    if (edit instanceof HTMLElement) {
      event.preventDefault()
      setEdited(Number(edit.dataset.category ?? 0))
      return
    }

    const kill = target.closest('.remove-category')
    if (kill instanceof HTMLElement) {
      event.preventDefault()
      /* Intrebarea din swal, cu acelasi text. */
      if (!window.confirm('Are you sure?\nYou will not be able to recover this category!')) return
      remove.mutate(Number(kill.dataset.category ?? 0))
      return
    }

    const box = target.closest('input.activate-category')
    if (box instanceof HTMLInputElement) {
      activate.mutate({ category: Number(box.value), active: box.checked })
    }
  }

  return (
    <div onClick={onClick}>
      <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create new category
      </button>
      <h4>
        <strong>Ticket</strong> categories
      </h4>
      <hr />
      <DataTable
        id={TABLE_ID}
        columns={columns}
        rows={rows}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.ID)}`)}
        emptyHint="A category groups the subcategories and operations a ticket can be about."
        details={(row) => <SubcategoriesPanel category={Number(row.ID)} />}
      />
      <NewCategoryModal
        open={creating}
        ticketTypes={shell.data.ticket_types}
        onClose={() => setCreating(false)}
        onSaved={redraw}
      />
      <CategoryModal
        category={edited}
        ticketTypes={shell.data.ticket_types}
        onClose={() => setEdited(0)}
        onSaved={redraw}
      />
    </div>
  )
}

function Forbidden() {
  return (
    <div className="alert alert-danger">
      <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
    </div>
  )
}

/**
 * Randul desfasurat: `categoryDetails()` randa `subcategories.php`, care isi
 * aducea singur lista cu `getCategorySubcategories` (`subcategoryList.php`).
 * Aici e o singura cerere, dar acelasi markup - `.list-group.subcategory-list`
 * cu cate un `a.list-group-item`, sau caseta "No subcategories found."
 *
 * Bifele subcategoriilor raman de citit: subcategoriile sunt ALT grup din harta
 * (`Ticket SUBCATEGORIES`), cu alt drept
 * (`settings.easeedeskticket_ticket_subcategories_settings.read`). Cine le
 * comuta de aici le-ar scrie cu dreptul categoriilor, si asa se pierd portile.
 */
export function SubcategoriesPanel({ category }: { category: number }) {
  const query = useQuery(categorySubcategoriesQuery(category))
  return (
    <div style={{ padding: '0px 15px' }}>
      <div style={{ marginBottom: 20, marginTop: 10 }} className="row">
        <div className="col-md-6">
          <h4>Subcategories</h4>
        </div>
        <div className="col-md-6 aright"></div>
      </div>
      <div className="list-group subcategory-list">
        {query.error ? (
          <LoadFailed error={query.error} onRetry={() => void query.refetch()} what="The subcategories" />
        ) : !query.data ? (
          <Spinner />
        ) : query.data.subcategories.length === 0 ? (
          <a href="#" onClick={(e) => e.preventDefault()}>
            <div className="alert alert-info">
              <i className="fas fa-info-circle" aria-hidden="true"></i> No subcategories found.
            </div>
          </a>
        ) : (
          query.data.subcategories.map((sub) => (
            <a
              key={sub.ID}
              href="#"
              className="list-group-item"
              style={{ padding: '10px 15px' }}
              onClick={(e) => e.preventDefault()}
            >
              <input type="checkbox" className="icheck-sub" value={sub.ID} checked={sub.active === 1} readOnly disabled />{' '}
              <label style={{ marginLeft: 10 }}>{sub.subcategory_name}</label>
            </a>
          ))
        )}
      </div>
    </div>
  )
}

/** Lista de tipuri a celor doua modale: acelasi `<select multiple>` din machete. */
function TicketTypesField({
  id,
  ticketTypes,
  value,
  onChange,
}: {
  id: string
  ticketTypes: CategoryTicketType[]
  value: number[]
  onChange(next: number[]): void
}) {
  return (
    <div className="form-group">
      <label htmlFor={id} className="control-label">
        Ticket types
      </label>
      <select
        id={id}
        className="form-control select-picker"
        name="ticket_types[]"
        data-live-search="true"
        data-selected-text-format="count > 3"
        multiple
        value={value.map(String)}
        onChange={(e) =>
          onChange(
            Array.from(e.target.selectedOptions)
              .map((o) => Number(o.value))
              .filter((n) => n > 0),
          )
        }
      >
        {ticketTypes.map((type) => (
          <option key={type.ID} value={type.ID}>
            {type.type_name}
          </option>
        ))}
      </select>
    </div>
  )
}

/** `newCategoryModal` din macheta, cu formularul lui `create_category`. */
export function NewCategoryModal({
  open,
  ticketTypes,
  onClose,
  onSaved,
}: {
  open: boolean
  ticketTypes: CategoryTicketType[]
  onClose(): void
  onSaved(): void
}) {
  const [name, setName] = useState('')
  const [types, setTypes] = useState<number[]>([])

  /* `hidden.bs.modal` golea formularul; aici il golim la deschidere, ca
     urmatoarea categorie sa nu porneasca de la ce a ramas. */
  useEffect(() => {
    if (open) {
      setName('')
      setTypes([])
    }
  }, [open])

  const create = useMutation({
    mutationFn: () => createCategory(name.trim(), types),
    onSuccess: () => {
      toast('New category added.', 'Success')
      onClose()
      onSaved()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal id="newCategoryModal" labelledBy="newCategoryModalLabel" open={open} onClose={onClose} title="New category">
      <form
        id="new-category-form"
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '') return
          create.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor="new-category-name" className="control-label">
            Category name
          </label>
          <input
            type="text"
            className="form-control input-sm"
            id="new-category-name"
            name="category_name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <TicketTypesField id="new-category-types" ticketTypes={ticketTypes} value={types} onChange={setTypes} />
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary submit-new-category" disabled={name.trim() === '' || create.isPending}>
            Create
          </button>
        </div>
      </form>
    </Modal>
  )
}

/**
 * `editCategoryModal` din macheta: `get_category` da numele si tipurile legate,
 * lista de tipuri vine din shell, iar Save trimite pe `save_category`.
 *
 * Lista de tipuri se rescrie INTREAGA, ca acolo: formularul le are pe toate pe
 * el, deci "niciun tip bifat" chiar inseamna niciun tip.
 */
export function CategoryModal({
  category,
  ticketTypes,
  onClose,
  onSaved,
}: {
  category: number
  ticketTypes: CategoryTicketType[]
  onClose(): void
  onSaved(): void
}) {
  const query = useQuery(categoryQuery(category))
  const [name, setName] = useState('')
  const [types, setTypes] = useState<number[]>([])

  /* Formularul se umple din raspuns, o data pe categorie deschisa. */
  useEffect(() => {
    if (query.data && query.data.ID === category) {
      setName(query.data.category_name)
      setTypes(query.data.types)
    }
  }, [query.data, category])

  const save = useMutation({
    mutationFn: () => saveCategory(category, name.trim(), types),
    onSuccess: () => {
      toast('Settings saved.', 'Success')
      onClose()
      onSaved()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal id="editCategoryModal" labelledBy="editCategoryModalLabel" open={category > 0} onClose={onClose} title="Edit category">
      {!query.data ? (
        <Spinner />
      ) : (
        <form
          id="edit-category-form"
          method="post"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim() === '') return
            save.mutate()
          }}
        >
          <input type="hidden" name="category" value={category} readOnly />
          <div className="form-group">
            <label htmlFor="category-name" className="control-label">
              Category name
            </label>
            <input
              type="text"
              className="form-control input-sm"
              id="category-name"
              name="category_name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <TicketTypesField
            id="update-category-ticket-types"
            ticketTypes={ticketTypes}
            value={types}
            onChange={setTypes}
          />
          <div className="form-group aright">
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>{' '}
            <button type="submit" className="btn btn-primary save-category" disabled={name.trim() === '' || save.isPending}>
              Save
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
